import React, { useState } from 'react';
import { attachmentApi } from '../../services/api';
import { ConfirmModal } from '../common/ConfirmModal';
import { useUpdateExpense } from '../../hooks/useExpenses';
import { useQueryClient } from 'react-query';

interface Attachment {
  attachmentId: string;
  filename: string;
  fileSize: number;
  fileType: string;
}

interface AttachmentViewerProps {
  isOpen: boolean;
  expenseId?: string;
  attachmentIds: string[];
  onClose: () => void;
  onAttachmentsChange?: (attachmentIds: string[]) => void;
  readOnly?: boolean;
}

export const AttachmentViewer: React.FC<AttachmentViewerProps> = ({
  isOpen,
  expenseId,
  attachmentIds,
  onClose,
  onAttachmentsChange,
  readOnly = false,
}) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    attachmentId: string;
    filename: string;
  }>({
    isOpen: false,
    attachmentId: '',
    filename: '',
  });
  
  const updateExpense = useUpdateExpense();
  const queryClient = useQueryClient();

  // Load attachments when modal opens
  React.useEffect(() => {
    if (isOpen && attachmentIds.length > 0) {
      loadAttachments();
    } else if (isOpen && attachmentIds.length === 0) {
      setAttachments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, attachmentIds]);

  const loadAttachments = async () => {
    setLoading(true);
    try {
      const loadedAttachments: Attachment[] = [];
      for (const id of attachmentIds) {
        try {
          const attachment = await attachmentApi.getDownloadUrl(id);
          loadedAttachments.push({
            attachmentId: attachment.attachmentId,
            filename: attachment.filename,
            fileSize: attachment.fileSize,
            fileType: attachment.fileType,
          });
        } catch (error) {
          console.error(`Error loading attachment ${id}:`, error);
        }
      }
      setAttachments(loadedAttachments);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType: string): string => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType === 'application/pdf') return '📄';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    return '📎';
  };

  const handleDownload = async (attachmentId: string) => {
    try {
      const attachment = await attachmentApi.getDownloadUrl(attachmentId);
      // Open download URL in new tab
      window.open(attachment.downloadUrl, '_blank');
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, attachmentId: string, filename: string) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmState({
      isOpen: true,
      attachmentId,
      filename,
    });
  };

  const handleDeleteConfirm = async () => {
    const { attachmentId } = confirmState;
    setConfirmState({ isOpen: false, attachmentId: '', filename: '' });
    
    setDeletingId(attachmentId);
    try {
      // Delete the attachment from S3
      await attachmentApi.delete(attachmentId);
      
      // Calculate new attachment IDs
      const newAttachmentIds = attachmentIds.filter(id => id !== attachmentId);
      
      // Update the expense in the backend if we have an expenseId
      if (expenseId) {
        await updateExpense.mutateAsync({
          expenseId,
          data: {
            attachmentIds: newAttachmentIds,
          },
        });
        
        // Force refetch of expenses to update the list immediately
        await queryClient.refetchQueries(['expenses'], { active: true });
        
        // If no more attachments, close the modal
        if (newAttachmentIds.length === 0) {
          onClose();
        } else {
          // Update local state to show remaining attachments
          setAttachments(prev => prev.filter(a => a.attachmentId !== attachmentId));
        }
      }
      
      // Notify parent
      onAttachmentsChange?.(newAttachmentIds);
    } catch (error) {
      console.error('Error deleting attachment:', error);
    } finally {
      setDeletingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        title="Eliminar Adjunto"
        message={`¿Estás seguro de que deseas eliminar el archivo "${confirmState.filename}"?\n\nEsta acción no se puede deshacer.`}
        confirmText="Eliminar"
        confirmColor="red"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmState({ isOpen: false, attachmentId: '', filename: '' })}
      />

      {/* Attachment Viewer Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 transform transition-all">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Archivos Adjuntos ({attachmentIds.length})
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : attachments.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No hay archivos adjuntos
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {attachments.map((attachment) => (
                  <div
                    key={attachment.attachmentId}
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <span className="text-2xl flex-shrink-0">
                        {getFileIcon(attachment.fileType)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {attachment.filename}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(attachment.fileSize)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                      {/* Download button */}
                      <button
                        onClick={() => handleDownload(attachment.attachmentId)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Descargar"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>

                      {/* Delete button - only if not read-only */}
                      {!readOnly && (
                        <button
                          onClick={(e) => handleDeleteClick(e, attachment.attachmentId, attachment.filename)}
                          disabled={deletingId === attachment.attachmentId}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                          title="Eliminar"
                        >
                          {deletingId === attachment.attachmentId ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Footer */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
